import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiAward, FiCrosshair, FiShield, FiTrendingUp } from 'react-icons/fi';

const BugBountySection = styled.section`
  padding: 100px 150px;
  background-color: var(--bg-secondary);
  
  @media (max-width: 768px) {
    padding: 80px 25px;
  }
`;

const BugBountyContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const BugBountyTitle = styled(motion.h2)`
  display: flex;
  align-items: center;
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 40px;
  color: var(--text-primary);
  
  &::after {
    content: "";
    display: block;
    width: 300px;
    height: 1px;
    margin-left: 20px;
    background-color: var(--bg-highlight);
  }
  
  @media (max-width: 768px) {
    font-size: 24px;
    
    &::after {
      width: 100px;
    }
  }
`;

const BugBountyDescription = styled(motion.p)`
  color: var(--text-secondary);
  font-size: 18px;
  margin-bottom: 40px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;
`;

const CardIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  box-shadow: 0 10px 25px rgba(2, 12, 27, 0.2);
  border: 1px solid var(--bg-highlight);
  transition: all 0.3s ease;
  
  svg {
    font-size: 28px;
    color: var(--accent-primary);
  }
`;

const BountyCard = styled(motion.div)`
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 30px;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  border: 1px solid var(--bg-highlight);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px -20px rgba(2, 12, 27, 0.7);
    border-color: var(--accent-primary);
    
    ${CardIcon} {
      transform: translateY(-5px);
      box-shadow: 0 8px 20px rgba(100, 255, 218, 0.3);
      border-color: var(--accent-primary);
    }
  }
`;

const CardTitle = styled.h3`
  color: var(--text-primary);
  font-size: 22px;
  margin-bottom: 15px;
  font-weight: 600;
`;

const CardDescription = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 15px;
`;

const CardStats = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: 20px;
  
  span {
    display: flex;
    align-items: center;
    margin-right: 20px;
    color: var(--accent-primary);
    font-family: var(--font-mono);
    font-size: 14px;
    
    svg {
      margin-right: 5px;
    }
  }
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.2,
      duration: 0.5
    }
  }
};

const cardVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

const BugBounty = () => {
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [descRef, descInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [cardsRef, cardsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const bounties = [
    {
      id: 1,
      company: "OpenAI",
      description: "Reported multiple vulnerabilities in OpenAI systems, demonstrating my expertise in identifying security flaws in advanced AI technologies.",
      achievement: "Hall of Fame",
      icon: <FiAward />,
      impact: "Privacy"
    },
    {
      id: 2,
      company: "Cloudflare",
      description: "Identified and reported critical vulnerabilities in Cloudflare services that could have resulted in significant financial loss if exploited.",
      achievement: "Hall of Fame",
      icon: <FiShield />,
      impact: "Financial"
    },
    {
      id: 3,
      company: "WhatsApp",
      description: "Through independent security research, discovered and responsibly disclosed a vulnerability that could have compromised the privacy of billions of WhatsApp users worldwide.",
      achievement: "Security Research",
      icon: <FiCrosshair />,
      impact: "Privacy"
    },
    {
      id: 4,
      company: "Google",
      description: "Vulnerability details cannot be disclosed at this time as the issue is currently being addressed and has not yet been fully resolved.",
      achievement: "Hall of Fame",
      icon: <FiShield />,
      impact: "Broken Access Control"
    }
  ];
  
  return (
    <BugBountySection id="bug-bounty">
      <BugBountyContainer>
        <BugBountyTitle
          ref={titleRef}
          initial={{ y: 20, opacity: 0 }}
          animate={titleInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          Bug Bounty
        </BugBountyTitle>
        
        <BugBountyDescription
          ref={descRef}
          initial={{ y: 20, opacity: 0 }}
          animate={descInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          I actively participate in bug bounty programs, helping organizations identify and fix security vulnerabilities before they can be exploited. 
          Here are some notable achievements from my bug bounty work:
        </BugBountyDescription>
        
        <CardsContainer
          ref={cardsRef}
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={cardsInView ? "visible" : "hidden"}
        >
          {bounties.map((bounty) => (
            <BountyCard
              key={bounty.id}
              variants={cardVariants}
              whileHover={{ y: -5, transition: { duration: 0.3 } }}
            >
              <CardIcon>
                {bounty.icon}
              </CardIcon>
              <CardTitle>{bounty.company}</CardTitle>
              <CardDescription>{bounty.description}</CardDescription>
              <div style={{ color: 'var(--accent-primary)', fontFamily: 'var(--font-mono)', fontSize: '14px', marginTop: '10px' }}>
                {bounty.achievement}
              </div>
              <CardStats>
                <span><FiTrendingUp /> {bounty.impact}</span>
              </CardStats>
            </BountyCard>
          ))}
        </CardsContainer>
      </BugBountyContainer>
    </BugBountySection>
  );
};

export default BugBounty; 